import { ShieldAlert } from 'lucide-react';
import React, { useCallback, useState } from 'react';

import { MakeCallModal } from '@/components/common/calls/make-call-modal/MakeCallModal';
import { SendFrontEmailDialog } from '@/components/matter/SendFrontEmailDialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Card } from '@/components/ui/card';
import {
  CLIENT_COMMS_EVENTS,
  CLIENT_COMMS_PROPERTIES,
} from '@/constants/analytics/client-communications-events';
import { usePostHogCapture } from '@/hooks/usePostHogCapture';
import { cn } from '@/lib/utils';
import { DetailedMatter } from '@/services/types/client-intake-types';

import {
  ClientCommunicationsProvider,
  useClientCommunications,
} from './ClientCommunicationsContext';
import { ClientCommunicationsHeader } from './ClientCommunicationsHeader';
import { CommunicationsContent } from './CommunicationsContent';
import { CommunicationsFooter } from './CommunicationsFooter';
import { CommunicationsTabNav } from './CommunicationsTabNav';
import { CommunicationDetailPane } from './detail-pane';
import { FollowUpReminder } from './shared/FollowUpReminder';

interface ClientCommunicationsTabInnerProps {
  matter: DetailedMatter;
  clientPhone?: string;
  clientEmail?: string;
}

const ClientCommunicationsTabInner: React.FC<
  ClientCommunicationsTabInnerProps
> = ({ matter, clientPhone, clientEmail }) => {
  const { activeTab, followUpStatus, refreshCommunications, isDetailPaneOpen } =
    useClientCommunications();

  const capture = usePostHogCapture();

  const [isEmailDialogOpen, setIsEmailDialogOpen] = useState(false);
  const [isCallModalOpen, setIsCallModalOpen] = useState(false);
  const [dncConfirmAction, setDncConfirmAction] = useState<
    'email' | 'call' | null
  >(null);

  const primaryClient = matter.clients?.[0];
  const clientName = primaryClient?.name || 'Client';
  const clientId = primaryClient?.id;
  const doNotContact = matter.contact_client_directly === false;

  const handleOpenEmailComposer = useCallback(() => {
    if (doNotContact) {
      setDncConfirmAction('email');
      // Track DNC blocked
      capture(CLIENT_COMMS_EVENTS.DNC_CONTACT_BLOCKED, {
        [CLIENT_COMMS_PROPERTIES.MATTER_ID]: matter.id,
        [CLIENT_COMMS_PROPERTIES.ACTION_BLOCKED]: 'email',
        [CLIENT_COMMS_PROPERTIES.DO_NOT_CONTACT]: true,
        [CLIENT_COMMS_PROPERTIES.PAGE]: 'matter_detail',
        [CLIENT_COMMS_PROPERTIES.COMPONENT]: 'client_communications',
      });
    } else {
      setIsEmailDialogOpen(true);
      // Track email compose opened
      capture(CLIENT_COMMS_EVENTS.EMAIL_COMPOSE_OPENED, {
        [CLIENT_COMMS_PROPERTIES.MATTER_ID]: matter.id,
        [CLIENT_COMMS_PROPERTIES.PAGE]: 'matter_detail',
        [CLIENT_COMMS_PROPERTIES.COMPONENT]: 'client_communications',
      });
    }
  }, [doNotContact, matter.id, capture]);

  const handleInitiateCall = useCallback(() => {
    if (doNotContact) {
      setDncConfirmAction('call');
      // Track DNC blocked
      capture(CLIENT_COMMS_EVENTS.DNC_CONTACT_BLOCKED, {
        [CLIENT_COMMS_PROPERTIES.MATTER_ID]: matter.id,
        [CLIENT_COMMS_PROPERTIES.ACTION_BLOCKED]: 'call',
        [CLIENT_COMMS_PROPERTIES.DO_NOT_CONTACT]: true,
        [CLIENT_COMMS_PROPERTIES.PAGE]: 'matter_detail',
        [CLIENT_COMMS_PROPERTIES.COMPONENT]: 'client_communications',
      });
    } else {
      setIsCallModalOpen(true);
      // Track call initiated
      capture(CLIENT_COMMS_EVENTS.CALL_INITIATED, {
        [CLIENT_COMMS_PROPERTIES.MATTER_ID]: matter.id,
        [CLIENT_COMMS_PROPERTIES.PAGE]: 'matter_detail',
        [CLIENT_COMMS_PROPERTIES.COMPONENT]: 'client_communications',
      });
    }
  }, [doNotContact, matter.id, capture]);

  const handleDncConfirm = useCallback(() => {
    if (dncConfirmAction === 'email') {
      setIsEmailDialogOpen(true);
      // Track email compose opened (after DNC confirm)
      capture(CLIENT_COMMS_EVENTS.EMAIL_COMPOSE_OPENED, {
        [CLIENT_COMMS_PROPERTIES.MATTER_ID]: matter.id,
        [CLIENT_COMMS_PROPERTIES.DO_NOT_CONTACT]: true,
        [CLIENT_COMMS_PROPERTIES.PAGE]: 'matter_detail',
        [CLIENT_COMMS_PROPERTIES.COMPONENT]: 'client_communications',
      });
    } else if (dncConfirmAction === 'call') {
      setIsCallModalOpen(true);
      // Track call initiated (after DNC confirm)
      capture(CLIENT_COMMS_EVENTS.CALL_INITIATED, {
        [CLIENT_COMMS_PROPERTIES.MATTER_ID]: matter.id,
        [CLIENT_COMMS_PROPERTIES.DO_NOT_CONTACT]: true,
        [CLIENT_COMMS_PROPERTIES.PAGE]: 'matter_detail',
        [CLIENT_COMMS_PROPERTIES.COMPONENT]: 'client_communications',
      });
    }
    setDncConfirmAction(null);
  }, [dncConfirmAction, matter.id, capture]);

  return (
    <div className="flex h-full">
      {/* Single pane layout - shows either list or detail view */}
      <Card
        className={cn(
          'flex flex-col overflow-hidden w-full',
          'border-zinc-200 shadow-none rounded-3xl p-0',
        )}
      >
        {/* Show detail pane when a communication is selected */}
        {isDetailPaneOpen ? (
          <CommunicationDetailPane />
        ) : (
          <>
            {/* Header with tabs and search */}
            <div className="flex-shrink-0 border-b border-zinc-100 bg-gray-100">
              <ClientCommunicationsHeader
                matter={matter}
                clientPhone={clientPhone}
                clientEmail={clientEmail}
                doNotContact={doNotContact}
                onOpenEmailComposer={handleOpenEmailComposer}
                onInitiateCall={handleInitiateCall}
              />
              <CommunicationsTabNav />
            </div>

            {/* Content area - scrollable */}
            <div className="flex-1 overflow-y-auto">
              <CommunicationsContent />
            </div>

            {/* Follow-up reminder - above footer */}
            {followUpStatus?.requires_follow_up && (
              <FollowUpReminder
                daysSinceContact={followUpStatus.days_since_last_contact}
                lastContactedAt={followUpStatus.last_contacted_at}
              />
            )}

            {/* Footer with input/actions */}
            <CommunicationsFooter
              activeTab={activeTab}
              matterId={matter.id}
              firmId={matter.firm?.id}
              clientId={clientId}
              clientPhone={clientPhone}
              clientEmail={clientEmail}
              clientName={clientName}
              doNotContact={doNotContact}
              smsConfigured={!!matter.firm?.sms_sender_number}
              onOpenEmailComposer={handleOpenEmailComposer}
              onInitiateCall={handleInitiateCall}
            />
          </>
        )}
      </Card>

      {/* Email Dialog */}
      <SendFrontEmailDialog
        open={isEmailDialogOpen}
        onClose={() => setIsEmailDialogOpen(false)}
        matterId={matter.id}
        defaultTo={clientEmail ? [clientEmail] : []}
        onSent={() => {
          // Refresh communications list after email is sent
          refreshCommunications();
        }}
      />

      {/* Call Modal */}
      <MakeCallModal
        isOpen={isCallModalOpen}
        onClose={() => setIsCallModalOpen(false)}
        matterId={matter.id}
        initialPhoneNumber={clientPhone}
        initialContactLabel={clientName}
        source="client_communications"
      />

      {/* Do Not Contact Confirmation Dialog */}
      <AlertDialog
        open={dncConfirmAction !== null}
        onOpenChange={(open) => !open && setDncConfirmAction(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <div className="flex items-center gap-2 text-red-600 mb-2">
              <ShieldAlert className="h-5 w-5" />
              <AlertDialogTitle className="text-red-600">
                Do Not Contact Warning
              </AlertDialogTitle>
            </div>
            <AlertDialogDescription className="text-zinc-600">
              This matter is marked as{' '}
              <span className="font-semibold text-red-600">
                Do Not Contact Client
              </span>
              . Are you sure you want to{' '}
              {dncConfirmAction === 'email' ? 'send an email' : 'make a call'}{' '}
              to <span className="font-semibold">{clientName}</span>?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDncConfirm}
              className="bg-red-600 hover:bg-red-700"
            >
              Yes, Contact Client
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

interface ClientCommunicationsTabProps {
  matter: DetailedMatter;
}

export const ClientCommunicationsTab: React.FC<
  ClientCommunicationsTabProps
> = ({ matter }) => {
  // Extract client contact info from the primary client
  const primaryClient = matter.clients?.[0];
  const clientPhone = primaryClient?.phone_numbers?.[0]?.phone_number;
  const clientEmail = primaryClient?.email;

  return (
    <div className="w-full max-w-[1000px] mx-auto h-full py-4 pb-8 px-1">
      <ClientCommunicationsProvider matterId={matter.id}>
        <ClientCommunicationsTabInner
          matter={matter}
          clientPhone={clientPhone}
          clientEmail={clientEmail}
        />
      </ClientCommunicationsProvider>
    </div>
  );
};

export default ClientCommunicationsTab;
